import { useMemo } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import { useCreateVenuaPlannerSerie } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { buildSerieFormSchema, emptySerieFormValues } from './serieFormSchema';
import { toCreateSerieRequest } from './toSerieRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
};

interface UseSerieFormOptions {
  /** Produkt, pod kterým série vzniká. */
  productId: string;
  /** Voláno po úspěšném uložení. */
  onSaved: () => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na konkrétní pole (např. síťová chyba). */
  onError?: (error: unknown) => void;
}

/**
 * Zapouzdřuje formulářovou logiku série – TanStack Form se Zod validací, create mutace a promítnutí
 * serverových validačních chyb do polí.
 */
export function useSerieForm({ productId, onSaved, onError }: UseSerieFormOptions) {
  const { t } = useTranslation('venua-planner');
  const schema = useMemo(() => buildSerieFormSchema(t), [t]);

  const createMutation = useCreateVenuaPlannerSerie();

  const form = useForm({
    defaultValues: emptySerieFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await createMutation.mutateAsync({ productId, data: toCreateSerieRequest(value) });
        onSaved();
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  return { form, isSubmitting: createMutation.isPending };
}

/** Typ TanStack Form instance sdílený do `SerieFormFields` (shodný s formulářem produktu). */
export type SerieFormApi = ReturnType<typeof useSerieForm>['form'];
